import { Trans, useLingui } from '@lingui/react';
import { useCompanyId } from '@startuptools/common/react-wrapper';
import { Alert } from '@startuptools/ui/Alert';
import { Card } from '@startuptools/ui/Card';
import { Link } from '@startuptools/ui/Link';
import { Stack } from '@startuptools/ui/Stack';
import { Typography } from '@startuptools/ui/Typography';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Table } from '@startuptools/ui/Table';
import { NumberFormat } from '@startuptools/ui/NumberFormat';
import { Box } from '@startuptools/ui/Box';
import { Button } from '@startuptools/ui/Button';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import { IconButton } from '@startuptools/ui/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import { Tooltip } from '@startuptools/ui/Tooltip';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { type ComponentProps, Suspense, useState } from 'react';
import { ParticipantsSummons } from './ParticipantsSummons';
import { useDeleteMeetingSummonsAttachmentMutation } from './EmailSummons.graphql';
import {
  GqlDocSystemTagKind,
  GqlBolagoFile,
  GqlMeeting,
  GqlMeetingHeldKind,
} from '../../../../graphql/base-types.graphql';
import { MeetingSummonsWithUploads } from '../../../common/components/documents/MeetingSummonsWithUploads';
import { allAccept, DocumentCreateDialog } from '@startuptools/business-ui/document/DocumentCreateDialog';
import { apiClient } from '../../../../common/http';
import { DocGenRelationName } from '@startuptools/common/doc-gen';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import { DisplayDocument } from '@startuptools/business-ui/document/Document';
import { BoardMeetingDocument } from '../../../board-meetings/BoardMeeting.graphql';
import { ShareholdersMeetingDocument } from '../../../shareholders-meetings/ShareholdersMeeting.graphql';

const MeetingStepEmailSummonsInner = ({
  meeting,
  refetch,
}: {
  meeting: Pick<GqlMeeting, 'id' | 'number' | 'startTime' | 'kind' | 'heldKind'> & {
    latestSummonsDocument?: Pick<GqlBolagoFile, 'id'> | null;
    meetingAppendices: DisplayDocument[];
    summonsAttachments: DisplayDocument[];
  };
  refetch: () => void;
}) => {
  const companyId = useCompanyId();
  const { i18n } = useLingui();
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [expandEmailAttachments, setExpandEmailAttachments] = useState(false);

  // eslint-disable-next-line sut/loading-mutation
  const [doDelete] = useDeleteMeetingSummonsAttachmentMutation({
    refetchQueries: [BoardMeetingDocument, ShareholdersMeetingDocument],
  });

  return (
    <>
      <Stack gap={3}>
        <Typography level="h4">
          <Trans id="Create and Send Summons" />
        </Typography>
        {meeting.heldKind === GqlMeetingHeldKind.PerCapsulam && (
          <Alert variant="outlined" color="neutral" startDecorator={<TaskAltIcon />}>
            <Trans
              id="The meeting is per capsulam. That means the meeting is not held physically but rather is only documented, normally no invitation is created. For more information,<link>read more here</link>"
              components={{
                link: (
                  <Link
                    href="https://startuptools.org/se/att-halla-bolagsstamma-per-capsulam"
                    target="_blank"
                    fontSize={14}
                  />
                ),
              }}
            />
          </Alert>
        )}
        <MeetingSummonsWithUploads meeting={meeting} refetch={refetch} />
        <Card>
          <Stack direction="row" gap={1} alignItems="center">
            <IconButton variant="outlined" onClick={() => setExpandEmailAttachments(!expandEmailAttachments)}>
              {expandEmailAttachments ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
            <Typography level="title-lg">
              <Trans id="Summons email attachments" />
            </Typography>
          </Stack>
          {expandEmailAttachments && (
            <>
              <Alert>
                <InfoOutlinedIcon />
                <Trans id="These files will be included in the summons email as attachments" />
              </Alert>
              <Box>
                <Button onClick={() => setShowUploadDialog(true)}>
                  <Trans id="Upload Email Attachment" />
                </Button>
              </Box>
              <Table sx={{ tableLayout: 'auto' }}>
                <tbody>
                  {meeting.summonsAttachments.map(d => (
                    <tr key={d.id}>
                      <td>
                        <Stack direction="row" gap={1} alignItems="center">
                          <InsertDriveFileIcon color="primary" /> {d.filename}
                        </Stack>
                      </td>
                      <td align="right">
                        <Stack direction="row" gap={1} alignItems="center" justifyContent="end">
                          {d.filesize && d.filesize > 10_000_000 && (
                            <Tooltip
                              title={
                                <Trans id="Some email providers have limits of 10MB per attachment, those might not receive the email because this file is bigger than that" />
                              }
                            >
                              <WarningAmberOutlinedIcon />
                            </Tooltip>
                          )}
                          <NumberFormat locale={i18n.locale} variant="filesize">
                            {d.filesize}
                          </NumberFormat>
                        </Stack>
                      </td>
                      <td align="right">
                        <IconButton
                          variant="outlined"
                          color="danger"
                          onClick={() => doDelete({ variables: { companyId, docId: d.id } })}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </>
          )}
        </Card>
        <Card>
          <ParticipantsSummons meetingId={meeting.id} documentId={meeting.latestSummonsDocument?.id} errors={null} />
        </Card>
      </Stack>
      {showUploadDialog && (
        <DocumentCreateDialog
          allowDigitalSignature={false}
          mode="upload"
          onClose={() => setShowUploadDialog(false)}
          upload={async ({ file }) => {
            await apiClient.uploadFile(
              file,
              file.name,
              companyId,
              { relation_name: DocGenRelationName.MeetingSummonsAttachment, relation_id: meeting.id },
              false,
              GqlDocSystemTagKind.Other,
            );
            refetch();
            setShowUploadDialog(false);
          }}
          showExistingDocWarning={false}
          showSigningWarning={false}
          acceptMimeTypes={allAccept}
        />
      )}
    </>
  );
};

export const MeetingStepEmailSummons = (props: ComponentProps<typeof MeetingStepEmailSummonsInner>) => {
  return (
    <Suspense>
      <MeetingStepEmailSummonsInner {...props} />
    </Suspense>
  );
};
